import React, { useMemo, useState, useCallback } from 'react';
import { View, Text, StyleSheet, LayoutChangeEvent } from 'react-native';
import moment from 'moment';
import Svg, { Defs, LinearGradient, Path, Stop, Line } from 'react-native-svg';
import { useTranslation, formatNumber, formatPercent, getLocale } from '../../shared/i18n';
import { horizontalLtrDirectionStyle } from '../../shared/i18n/rtl';
import { useRtl } from '../../shared/i18n/useRtl';
import type { ThemeColors } from '../../shared/theme';
import type { DayMacroSnapshot } from '../../data/domains/dashboard';
import { ContentCard } from '../../components/common/ContentCard';
import { DASH_STUDIO_RADIUS, studioCardShell } from '../../shared/theme/studio/dashboardConstants';
import { useDashboardVariant } from '../../data/domains/config';
import { dashboardSet1CardShell } from './variants/set1/dashboardSet1Styles';
import { appTheme } from '../../shared/theme';
import { hexToRgba } from '../../shared/theme/colorUtils';

const CHART_H = 132;
const CHART_PAD = { l: 4, r: 4, t: 8, b: 22 };

function calorieFromMacros(p: number, c: number, f: number) {
  return p * 4 + c * 4 + f * 9;
}

const MACRO_ROW_KEYS = ['fat', 'protein', 'carbs'] as const;

interface DashboardMacroWeekSectionProps {
  history: DayMacroSnapshot[];
  colors: ThemeColors;
  loaded: boolean;
  dateFrom: string;
  dateTo: string;
}

function buildLinePath(pts: { x: number; y: number }[]) {
  if (pts.length === 0) {
    return '';
  }
  if (pts.length === 1) {
    const p = pts[0]!;
    return `M ${p.x} ${p.y}`;
  }
  let d = `M ${pts[0]!.x} ${pts[0]!.y}`;
  for (let i = 1; i < pts.length; i++) {
    d += ` L ${pts[i]!.x} ${pts[i]!.y}`;
  }
  return d;
}

function buildAreaPath(pts: { x: number; y: number }[], bottomY: number) {
  if (pts.length === 0) {
    return '';
  }
  if (pts.length === 1) {
    const p = pts[0]!;
    return `M ${p.x} ${bottomY} L ${p.x} ${p.y} L ${p.x} ${bottomY} Z`;
  }
  const line = buildLinePath(pts);
  if (!line) {
    return '';
  }
  return `${line} L ${pts[pts.length - 1]!.x} ${bottomY} L ${pts[0]!.x} ${bottomY} Z`;
}

export function DashboardMacroWeekSection({ history, colors, loaded, dateFrom, dateTo }: DashboardMacroWeekSectionProps) {
  const { t } = useTranslation();
  const rtl = useRtl();
  const dashboard_variant = useDashboardVariant();
  const is_set1 = dashboard_variant === 'set1';
  const card_shell = is_set1 ? dashboardSet1CardShell(colors) : studioCardShell(colors);
  const text_align: 'left' | 'right' = rtl.isRTL ? 'right' : 'left';
  const [chart_w, set_chart_w] = useState(0);
  const on_chart_layout = useCallback((e: LayoutChangeEvent) => {
    set_chart_w(e.nativeEvent.layout.width);
  }, []);
  const solid: Record<(typeof MACRO_ROW_KEYS)[number], string> = useMemo(
    () => ({
      fat: colors.fats,
      protein: colors.protein,
      carbs: colors.carbs,
    }),
    [colors],
  );
  const locale = getLocale();
  const date_range_label = useMemo(() => {
    const loc = locale === 'ar' ? 'ar' : 'en';
    try {
      moment.locale(loc);
    } catch {
      moment.locale('en');
    }
    const from_d = moment(dateFrom, 'YYYY-MM-DD');
    const to_d = moment(dateTo, 'YYYY-MM-DD');
    const from_fmt = formatNumber(from_d.date()) + ' ' + from_d.format('MMM');
    const to_fmt = formatNumber(to_d.date()) + ' ' + to_d.format('MMM');
    return t('dashboard.macrosDateRange').replace('{{from}}', from_fmt).replace('{{to}}', to_fmt);
  }, [dateFrom, dateTo, t, locale]);
  const formatNum = formatNumber;
  const { p_frac, c_frac, f_frac, total_calories, avg_calories, total_fat_g, total_protein_g, total_carbs_g } = useMemo(() => {
    if (!history.length) {
      return {
        p_frac: 1 / 3,
        c_frac: 1 / 3,
        f_frac: 1 / 3,
        total_calories: 0,
        avg_calories: 0,
        total_fat_g: 0,
        total_protein_g: 0,
        total_carbs_g: 0,
      };
    }
    let p_cal = 0;
    let c_cal = 0;
    let f_cal = 0;
    let sum_fat_g = 0;
    let sum_protein_g = 0;
    let sum_carbs_g = 0;
    for (const day of history) {
      const pg = day.protein ?? 0;
      const cg = day.carbs ?? 0;
      const fg = day.fats ?? 0;
      p_cal += calorieFromMacros(pg, 0, 0);
      c_cal += calorieFromMacros(0, cg, 0);
      f_cal += calorieFromMacros(0, 0, fg);
      sum_protein_g += pg;
      sum_carbs_g += cg;
      sum_fat_g += fg;
    }
    const total = p_cal + c_cal + f_cal || 1;
    const total_cal = Math.round(p_cal + c_cal + f_cal);
    return {
      p_frac: p_cal / total,
      c_frac: c_cal / total,
      f_frac: f_cal / total,
      total_calories: total_cal,
      avg_calories: history.length > 0 ? Math.round(total_cal / history.length) : 0,
      total_fat_g: Math.round(sum_fat_g),
      total_protein_g: Math.round(sum_protein_g),
      total_carbs_g: Math.round(sum_carbs_g),
    };
  }, [history]);

  const bars = useMemo(
    () => [
      { key: MACRO_ROW_KEYS[0] as (typeof MACRO_ROW_KEYS)[number], frac: f_frac, value_g: total_fat_g, label: t('dashboard.fat') },
      { key: MACRO_ROW_KEYS[1] as (typeof MACRO_ROW_KEYS)[number], frac: p_frac, value_g: total_protein_g, label: t('dashboard.protein') },
      { key: MACRO_ROW_KEYS[2] as (typeof MACRO_ROW_KEYS)[number], frac: c_frac, value_g: total_carbs_g, label: t('dashboard.carbs') },
    ],
    [f_frac, p_frac, c_frac, total_fat_g, total_protein_g, total_carbs_g, t],
  );

  const daily_cals = useMemo(() => history.map(d => Math.max(0, d.calories ?? 0)), [history]);
  const day_labels = useMemo(() => {
    const loc = locale === 'ar' ? 'ar' : 'en';
    try {
      moment.locale(loc);
    } catch {
      moment.locale('en');
    }
    return history.map(d => moment(d.date, 'YYYY-MM-DD').format('ddd'));
  }, [history, locale]);

  const chart_geo = useMemo(() => {
    const w = Math.max(0, chart_w);
    if (w < 8 || daily_cals.length === 0) {
      return { area_d: '', line_d: '', y_grid: [] as number[] };
    }
    const inner_w = w - CHART_PAD.l - CHART_PAD.r;
    const inner_h = CHART_H - CHART_PAD.t - CHART_PAD.b;
    const max_v = Math.max(1, ...daily_cals);
    const n = daily_cals.length;
    const step = n <= 1 ? 0 : inner_w / (n - 1);
    const pts = daily_cals.map((v, i) => ({
      x: CHART_PAD.l + i * step,
      y: CHART_PAD.t + inner_h * (1 - v / max_v),
    }));
    const bottomY = CHART_H - CHART_PAD.b;
    const area_d = buildAreaPath(pts, bottomY);
    const line_d = buildLinePath(pts);
    const y_grid = [0.5, 1].map(frac => CHART_PAD.t + inner_h * (1 - frac));
    return { area_d, line_d, y_grid };
  }, [chart_w, daily_cals]);

  const grad_id = 'macroAreaGrad';

  return (
    <ContentCard
      variant="outlined"
      borderless
      padding="lg"
      style={[card_shell, { borderRadius: DASH_STUDIO_RADIUS }, !is_set1 ? { backgroundColor: colors.surface } : null]}>
      {!is_set1 ? (
        <Text
          style={[
            styles.sectionKicker,
            { color: colors.textLight, textAlign: text_align, writingDirection: rtl.isRTL ? 'rtl' : 'ltr' },
          ]}
          numberOfLines={1}
          accessibilityRole="header">
          {t('dashboard.macrosThisWeek')}
        </Text>
      ) : null}
      <Text
        style={[
          styles.rangeTitle,
          { color: colors.text, textAlign: text_align, writingDirection: rtl.isRTL ? 'rtl' : 'ltr' },
        ]}>
        {date_range_label}
      </Text>
      <Text
        style={[
          styles.subline,
          { color: colors.textSecondary, textAlign: text_align, writingDirection: rtl.isRTL ? 'rtl' : 'ltr' },
        ]}>
        {t('dashboard.calorieSplitFromWeek')}
      </Text>
      {loaded ? (
        <View style={styles.body}>
          <View style={styles.metricsRow}>
            <View style={[styles.metricPill, styles.metricPillMain, { backgroundColor: hexToRgba(colors.text, 0.05) as string }]}>
              <View style={styles.inlineKcal}>
                <Text style={[styles.metricValue, { color: colors.text }]}>{total_calories > 0 ? formatNum(total_calories) : '—'}</Text>
                <Text style={[styles.metricUnit, { color: colors.textLight }]}>{' '}{t('dashboard.kcal')}</Text>
              </View>
            </View>
            {history.length > 0 && avg_calories > 0 && (
              <View
                style={[
                  styles.metricPill,
                  styles.metricPillCol,
                  { backgroundColor: hexToRgba(colors.primary, 0.1) as string },
                ]}>
                <Text style={[styles.metricSmallLabel, { color: colors.textSecondary }]}>{t('dashboard.avgPerDay')}</Text>
                <Text style={[styles.metricValueSm, { color: colors.primary }]}>
                  {formatNum(avg_calories)} {t('dashboard.kcal')}
                </Text>
              </View>
            )}
          </View>

          <View style={[styles.chartFrame, { backgroundColor: colors.surfaceLight }]} onLayout={on_chart_layout}>
            {chart_w > 0 && (
              <Svg width={chart_w} height={CHART_H}>
                <Defs>
                  <LinearGradient id={grad_id} x1="0" y1="0" x2="0" y2="1">
                    <Stop offset="0" stopColor={colors.primary} stopOpacity="0.35" />
                    <Stop offset="1" stopColor={colors.primary} stopOpacity="0.02" />
                  </LinearGradient>
                </Defs>
                {chart_geo.y_grid.map((gy, i) => (
                  <Line
                    key={i}
                    x1={CHART_PAD.l}
                    x2={chart_w - CHART_PAD.r}
                    y1={gy}
                    y2={gy}
                    stroke={hexToRgba(colors.text, 0.06) as string}
                    strokeWidth={1}
                    strokeDasharray="4 6"
                  />
                ))}
                {chart_geo.area_d ? <Path d={chart_geo.area_d} fill={`url(#${grad_id})`} /> : null}
                {chart_geo.line_d ? (
                  <Path
                    d={chart_geo.line_d}
                    fill="none"
                    stroke={colors.primary}
                    strokeWidth={2.5}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                ) : null}
              </Svg>
            )}
            <View style={styles.xAxis}>
              {day_labels.map((label, i) => (
                <Text key={i} style={[styles.xLab, { color: colors.textLight, flex: 1, textAlign: 'center' }]} numberOfLines={1}>
                  {label}
                </Text>
              ))}
            </View>
          </View>
          <View style={[styles.splitBar, horizontalLtrDirectionStyle, { backgroundColor: hexToRgba(colors.text, 0.08) as string }]}>
            {bars.map((b, i) => {
              const w = `${Math.max(0, Math.min(1, b.frac)) * 100}%` as const;
              const radiusStyle =
                i === 0
                  ? { borderTopLeftRadius: 10, borderBottomLeftRadius: 10 }
                  : i === bars.length - 1
                    ? { borderTopRightRadius: 10, borderBottomRightRadius: 10 }
                    : {};
              return <View key={b.key} style={[{ width: w, minWidth: 3, backgroundColor: solid[b.key] }, styles.splitSeg, radiusStyle]} />;
            })}
          </View>
          <View style={[styles.macroMetricsRow, horizontalLtrDirectionStyle]}>
            {bars.map(b => (
              <View key={b.key} style={styles.macroMetricCol}>
                <View style={[styles.macroMetricDot, { backgroundColor: solid[b.key] }]} />
                <Text
                  style={[
                    styles.macroMetricLabel,
                    { color: colors.text, writingDirection: rtl.isRTL ? 'rtl' : 'ltr' },
                  ]}
                  numberOfLines={1}>
                  {b.label}
                </Text>
                <Text
                  style={[
                    styles.macroMetricValue,
                    { color: colors.textSecondary, writingDirection: rtl.isRTL ? 'rtl' : 'ltr' },
                  ]}
                  numberOfLines={1}>
                  {b.value_g > 0 ? `${formatNum(b.value_g)}g` : '—'}
                </Text>
                <Text
                  style={[
                    styles.macroMetricPct,
                    { color: colors.textLight, writingDirection: rtl.isRTL ? 'rtl' : 'ltr' },
                  ]}
                  numberOfLines={1}>
                  {formatPercent(Math.round(b.frac * 100))}
                </Text>
              </View>
            ))}
          </View>
        </View>
      ) : (
        <Text style={[styles.placeholder, { color: colors.textLight, textAlign: text_align, writingDirection: rtl.isRTL ? 'rtl' : 'ltr' }]}>
          {t('common.loading')}
        </Text>
      )}
    </ContentCard>
  );
}

const styles = StyleSheet.create({
  sectionKicker: {
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 0.7,
    textTransform: 'uppercase' as const,
    marginBottom: 6,
  },
  rangeTitle: {
    fontSize: 18,
    fontWeight: '700',
    letterSpacing: -0.2,
    marginBottom: 4,
  },
  subline: {
    ...appTheme.typography.captionSmall,
    fontSize: 12,
    lineHeight: 16,
    marginBottom: appTheme.spacing.md,
    opacity: 0.9,
  },
  body: { width: '100%', gap: appTheme.spacing.md },
  metricsRow: { flexDirection: 'row', flexWrap: 'wrap', gap: appTheme.spacing.sm, alignItems: 'stretch' },
  metricPill: {
    paddingVertical: appTheme.spacing.sm,
    paddingHorizontal: appTheme.spacing.md,
    borderRadius: 14,
    minWidth: 0,
  },
  metricValue: { fontSize: 22, fontWeight: '800', letterSpacing: -0.3 },
  metricUnit: { fontSize: 12, fontWeight: '600', marginTop: 2 },
  metricSmallLabel: { fontSize: 10, fontWeight: '700', textTransform: 'uppercase' as const, letterSpacing: 0.5, marginBottom: 2 },
  metricValueSm: { fontSize: 15, fontWeight: '800' },
  chartFrame: {
    borderRadius: 16,
    overflow: 'hidden',
    minHeight: CHART_H + 20,
  },
  xAxis: { flexDirection: 'row', paddingHorizontal: 2, paddingBottom: 6, paddingTop: 2 },
  metricPillCol: { flexDirection: 'column', alignItems: 'flex-start' },
  xLab: { fontSize: 9, fontWeight: '800', textTransform: 'uppercase' as const, letterSpacing: 0.4, opacity: 0.75 },
  inlineKcal: { flexDirection: 'row', alignItems: 'baseline', flexWrap: 'wrap' },
  metricPillMain: { flexDirection: 'row', alignItems: 'center' },
  splitBar: { flexDirection: 'row', width: '100%', height: 12, borderRadius: 10, overflow: 'hidden' },
  splitSeg: { height: '100%' },
  macroMetricsRow: {
    flexDirection: 'row',
    width: '100%',
    marginTop: appTheme.spacing.sm,
  },
  macroMetricCol: {
    flex: 1,
    minWidth: 0,
    alignItems: 'center',
    paddingHorizontal: appTheme.spacing.xs,
    gap: 3,
  },
  macroMetricDot: { width: 8, height: 8, borderRadius: 4, marginBottom: 1 },
  macroMetricLabel: {
    fontSize: 11,
    fontWeight: '800',
    letterSpacing: 0.2,
    textAlign: 'center',
    width: '100%',
  },
  macroMetricValue: {
    fontSize: 13,
    fontWeight: '800',
    letterSpacing: -0.2,
    textAlign: 'center',
    width: '100%',
  },
  macroMetricPct: {
    fontSize: 11,
    fontWeight: '600',
    textAlign: 'center',
    width: '100%',
  },
  placeholder: { fontSize: 14, paddingVertical: 24 },
});
